import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import type { PostCard_PostData } from '~publish/components/PostCard'
import { getPostDisplayDate } from '~publish/helpers/post'

type SchedulablePost = PostCard_PostData & { dueAt: string }
type DatedContentItem = ContentItemDraft & { targetDate: string }

export type ScheduledEntry =
  | { kind: 'post'; date: string; post: SchedulablePost }
  | { kind: 'contentItem'; date: string; item: DatedContentItem }

/**
 * The date queue for the drafts view: scheduled posts plus channel-less drafts
 * that have a targetDate, ordered by date so the drafts land in the right day.
 *
 * With no dated drafts to weave in, the posts keep their query order — this
 * matters for other tabs (e.g. 'sent', most-recent first) where re-sorting
 * ascending would reverse the list.
 *
 * Posts and drafts paginate independently, so mid-load a draft can sit among
 * fewer posts than its final position; it settles as more pages arrive.
 */
export function buildScheduledEntries(
  scheduledPosts: SchedulablePost[],
  datedContentItems: DatedContentItem[],
): ScheduledEntry[] {
  const postEntries = scheduledPosts.map(
    (post): ScheduledEntry => ({
      kind: 'post',
      date: getPostDisplayDate(post),
      post,
    }),
  )

  if (datedContentItems.length === 0) {
    return postEntries
  }

  const itemEntries = datedContentItems.map(
    (item): ScheduledEntry => ({
      kind: 'contentItem',
      date: item.targetDate,
      item,
    }),
  )

  return [...postEntries, ...itemEntries].toSorted(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )
}
